import { type ClassValue } from 'cn'
import {
  type ClassGroupDef,
  type CnConfig,
  createCn,
  type DefaultClassGroupIds,
  defaultConfig,
  type DefaultThemeGroupIds,
  fromTheme,
  mergeConfigs,
  validators,
} from 'cn/config'
import {
  checkIsLengthWithUnit,
  checkIsPxOrPxEm,
  checkIsSemanticToken,
} from './classNameValidators.ts'

// Some properties won't have conflicts easily, so we can use wildcard,
// or a validate function to differentiate them.
const getPrimitiveAndSemanticLengthKeys = () =>
  [
    'none',
    'full',
    'auto',
    'min',
    'max',
    'fit',
    checkIsLengthWithUnit,
    checkIsSemanticToken,
  ] as const

const getNumber = () =>
  [validators.isNumber, validators.isArbitraryNumber] as const

export type AdditionalClassGroupIds =
  | 'type-scale'
  | 'slide-out-to-top'
  | 'slide-out-to-right'
  | 'slide-out-to-bottom'
  | 'slide-out-to-left'
  | 'slide-in-from-top'
  | 'slide-in-from-right'
  | 'slide-in-from-bottom'
  | 'slide-in-from-left'
  | 'fade-in'
  | 'fade-out'
  | 'zoom-in'
  | 'zoom-out'
  | 'animate-entering'
  | 'animation-duration'

export type AdditionalThemeGroupIds = 'sizing'

export type DesignSystemClassGroupIds =
  | DefaultClassGroupIds
  | AdditionalClassGroupIds
export type DesignSystemThemeGroupIds =
  | DefaultThemeGroupIds
  | AdditionalThemeGroupIds
export type DesignSystemTwMergeConfig = CnConfig

export type IntDesignSystemThemeGroupIds = DesignSystemThemeGroupIds

type TwMergeConfigExtension<
  ClassGroupIds extends string,
  ThemeGroupIds extends string,
> = {
  extend?: {
    theme?: Partial<Record<ThemeGroupIds, readonly ClassGroupDef[]>>
    classGroups?: Partial<Record<ClassGroupIds, readonly ClassGroupDef[]>>
    conflictingClassGroups?: Partial<
      Record<ClassGroupIds, readonly ClassGroupIds[]>
    >
  }
}

const fromThemeGroup = (key: DesignSystemThemeGroupIds) => fromTheme(key)

const BORDER_WIDTH_TOKENS = [
  'none',
  '100',
  '150',
  '200',
  '250',
  '300',
  'default',
  'xs',
  'sm',
  'icons-contained-xs',
  'icons-contained-sm',
  'icons-contained-md',
  'icons-contained-lg',
  'icons-contained-xl',
  'icons-contained-2xl',
  'icons-standalone-xs',
  'icons-standalone-sm',
  'icons-standalone-md',
  'icons-standalone-lg',
  'icons-standalone-xl',
  'icons-standalone-2xl',
  'icons-nav-bar',
]

const BORDER_RADIUS_TOKENS = [
  '0.5',
  '1',
  '2',
  '3',
  '4',
  '6',
  'full',
  'default',
  'buttons',
  'avatars',
  'icons',
]

const TYPE_SCALE_CLASS_TOKENS = [
  'base-display',
  'base-headings',
  'base-labels',
  'base-text',
  'marketing-display',
  'marketing-headings',
  'marketing-labels',
  'marketing-text',
]

export const CHEDDAR_TW_MERGE_CONFIG = mergeConfigs(defaultConfig(), {
  extend: {
    theme: {
      spacing: getPrimitiveAndSemanticLengthKeys(),
      sizing: getPrimitiveAndSemanticLengthKeys(),
      radius: BORDER_RADIUS_TOKENS,
    },
    classGroups: {
      'min-w': [{ 'min-w': [fromThemeGroup('sizing')] }],
      'max-w': [{ 'max-w': [fromThemeGroup('sizing')] }],
      'min-h': [{ 'min-h': [fromThemeGroup('sizing')] }],
      'max-h': [{ 'max-h': [fromThemeGroup('sizing')] }],
      'ring-color': [{ ring: [fromThemeGroup('color')] }],
      'border-w': [{ 'border-w': BORDER_WIDTH_TOKENS }],
      'border-w-x': [{ 'border-x-w': BORDER_WIDTH_TOKENS }],
      'border-w-y': [{ 'border-y-w': BORDER_WIDTH_TOKENS }],
      'border-w-s': [{ 'border-s-w': BORDER_WIDTH_TOKENS }],
      'border-w-e': [{ 'border-e-w': BORDER_WIDTH_TOKENS }],
      'border-w-t': [{ 'border-t-w': BORDER_WIDTH_TOKENS }],
      'border-w-r': [{ 'border-r-w': BORDER_WIDTH_TOKENS }],
      'border-w-b': [{ 'border-b-w': BORDER_WIDTH_TOKENS }],
      'border-w-l': [{ 'border-l-w': BORDER_WIDTH_TOKENS }],
      'ring-w': [
        { 'ring-w': BORDER_WIDTH_TOKENS, 'inset-ring-w': BORDER_WIDTH_TOKENS },
      ],
      'outline-color': [{ outline: [fromThemeGroup('color')] }],
      'outline-w': [{ 'outline-w': BORDER_WIDTH_TOKENS }],
      'divide-x': [{ 'divide-x-w': BORDER_WIDTH_TOKENS }],
      'divide-y': [{ 'divide-y-w': BORDER_WIDTH_TOKENS }],
      'font-size': [
        { font_size: [checkIsPxOrPxEm, validators.isArbitraryLength] },
      ],
      'font-family': [
        {
          font_family: [
            'inter',
            'earn_in_mori',
            'fallback-sans',
            ...TYPE_SCALE_CLASS_TOKENS,
          ],
        },
      ],
      'font-weight': [
        {
          font_weight: [
            'regular',
            'semibold',
            ...TYPE_SCALE_CLASS_TOKENS,
            validators.isArbitraryNumber,
          ],
        },
      ],
      tracking: [
        {
          letter_spacing: [
            'none',
            ...TYPE_SCALE_CLASS_TOKENS,
            validators.isTshirtSize,
          ],
        },
      ],
      leading: [
        { line_height: [checkIsPxOrPxEm, validators.isArbitraryLength] },
      ],
      'type-scale': [{ typo: [checkIsSemanticToken] }],
      // Support legacy classes before the migration
      shadow: [
        {
          shadow: [
            'controls-selected-hover',
            'controls-selected-focus',
            'controls-unselected-hover',
            'controls-unselected-focus',
          ],
        },
      ],
      'slide-out-to-top': [{ 'slide-out-to-top': [fromThemeGroup('spacing')] }],
      'slide-out-to-right': [
        { 'slide-out-to-right': [fromThemeGroup('spacing')] },
      ],
      'slide-out-to-bottom': [
        { 'slide-out-to-bottom': [fromThemeGroup('spacing')] },
      ],
      'slide-out-to-left': [
        { 'slide-out-to-left': [fromThemeGroup('spacing')] },
      ],
      'slide-in-from-top': [
        { 'slide-in-from-top': [fromThemeGroup('spacing')] },
      ],
      'slide-in-from-right': [
        { 'slide-in-from-right': [fromThemeGroup('spacing')] },
      ],
      'slide-in-from-bottom': [
        { 'slide-in-from-bottom': [fromThemeGroup('spacing')] },
      ],
      'slide-in-from-left': [
        { 'slide-in-from-left': [fromThemeGroup('spacing')] },
      ],
      'fade-in': [{ 'fade-in': getNumber() }],
      'fade-out': [{ 'fade-out': getNumber() }],
      'zoom-in': [{ 'zoom-in': getNumber() }],
      'zoom-out': [{ 'zoom-out': getNumber() }],
      'animate-entering': [{ animate: ['in', 'out'] }],
      'animation-duration': [{ 'animation-duration': getNumber() }],
    },
    conflictingClassGroups: {
      'type-scale': [
        'font-size',
        'font-family',
        'font-weight',
        'leading',
        'tracking',
        'text-transform',
      ],
      'slide-out-to-top': ['slide-out-to-bottom'],
      'slide-out-to-bottom': ['slide-out-to-top'],
      'slide-out-to-left': ['slide-out-to-right'],
      'slide-out-to-right': ['slide-out-to-left'],
      'slide-in-from-top': ['slide-in-from-bottom'],
      'slide-in-from-bottom': ['slide-in-from-top'],
      'slide-in-from-left': ['slide-in-from-right'],
      'slide-in-from-right': ['slide-in-from-left'],
    },
  },
} satisfies TwMergeConfigExtension<
  DesignSystemClassGroupIds,
  DesignSystemThemeGroupIds
>)

export type IntAdditionalClassGroupIds =
  | 'field-sizing'
  | 'bg-layered'
  | 'bg-layer-t'
  | 'bg-layer-b'

export type IntDesignSystemClassGroupIds =
  | DesignSystemClassGroupIds
  | IntAdditionalClassGroupIds

/**
 * Create a new merge config that includes the additional class groups.
 *
 * For config reference,
 * @see https://github.com/shadcn-ui/cn#custom-themes
 */
export const INT_DESIGN_SYSTEM_TW_MERGE_CONFIG = mergeConfigs(
  CHEDDAR_TW_MERGE_CONFIG,
  {
    extend: {
      classGroups: {
        appearance: [{ appearance: ['textfield'] }],
        'text-color': [{ text: ['foreground-potential-issue'] }],
        'bg-layered': [{ bg: ['layered'] }],
        'bg-layer-t': [{ 'bg-layer-t': [fromThemeGroup('color')] }],
        'bg-layer-b': [{ 'bg-layer-b': [fromThemeGroup('color')] }],
      },
      conflictingClassGroups: {
        'bg-layered': ['bg-image', 'bg-color'],
        'bg-image': ['bg-layered', 'bg-layer-t', 'bg-layer-b'],
        'bg-color': ['bg-layered', 'bg-layer-t', 'bg-layer-b'],
      },
    },
  } satisfies TwMergeConfigExtension<
    IntDesignSystemClassGroupIds,
    IntDesignSystemThemeGroupIds
  >,
)

export type CnParam = ClassValue

export type WithCnClassNameProp = {
  className?: CnParam
}

export type OverrideClassNameWithCnParam<T> = Omit<T, 'className'> &
  WithCnClassNameProp

/**
 * Join and merge tailwind classNames without conflicts.
 *
 * @example
 * cn('text-center', 'text-left') // 'text-left'
 */
export const cn = createCn(() => INT_DESIGN_SYSTEM_TW_MERGE_CONFIG)
